import {
  ConfigStateService,
  DynamicLayoutComponent,
  PermissionService,
  ReplaceableComponentsService,
  RoutesService,
  TimezoneService,
} from '@abp/ng.core';
import { eIdentityRouteNames } from '@volo/abp.ng.identity/config';
import {
  InternetConnectionStatusComponent,
  LoaderBarComponent,
  NavItem,
  NavItemsService,
  UserMenu,
  UserMenuService,
} from '@abp/ng.theme.shared';
import { Component, inject } from '@angular/core';
import { GdprCookieConsentComponent } from '@volo/abp.ng.gdpr/config';
import { SettingTabsService } from '@abp/ng.setting-management/config';
import { IntegrationSettingsComponent } from './administration/settings/integration-settings/integration-settings.component';
import { eThemeLeptonXComponents } from '@volosoft/abp.ng.theme.lepton-x';
import { FooterComponent } from './shared/components/footer/footer.component';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { UserService } from 'src/core/services/user/user.service';
import { AiDisclaimerGateService } from 'src/core/services/user/ai-disclaimer-gate.service';
import { AiDisclaimerModalComponent } from './shared/components/ai-disclaimer-modal/ai-disclaimer-modal.component';
import { OAuthService } from 'angular-oauth2-oidc';
import { DataImportComponent } from './administration/settings/data-import/data-import.component';
import { PhoneNumbersMainComponent } from './administration/settings/phone-numbers/components/phone-numbers-main/phone-numbers-main.component';
import { VersionModalComponent } from './shared/components/version-modal/version-modal.component';
import { Toast } from 'primeng/toast';
import { ConfirmDialogHostComponent } from 'src/core/services/confirmation/confirm-dialog-host/confirm-dialog-host.component';
import { UserSkillDialogComponent } from './features/agent-management/components/user-skill-dialog/user-skill-dialog.component';
import { AssignQueueDialogComponent } from './features/agent-management/components/assign-queue-dialog/assign-queue-dialog.component';
import { RejectCallDialogHostComponent } from './shared/agent-desktop/components/reject-call-dialog/reject-call-dialog-host.component';
import { AgentStatusToolbarComponent } from './shared/components/agent-status-toolbar/components/agent-status-toolbar/agent-status-toolbar.component';
import { QuickAvailableComponent } from './shared/components/agent-status-toolbar/components/quick-available/quick-available.component';
import { NotificationBellComponent } from './shared/components/notification-bell/notification-bell.component';
import { AgentStatusAccessService } from 'src/core/services/agent-status/agent-status-access.service';
import { filter, firstValueFrom } from 'rxjs';
import { WorkEnvironmentSettingsComponent } from './administration/settings/work-environment/work-environment-settings.component';
import { QueueManagementComponent } from './administration/settings/queues/queue-management.component';
import { LocalizationMainComponent } from './administration/settings/localization/localization-main.component';
import { ScheduledCallSettingsComponent } from './administration/settings/scheduled-calls/scheduled-call-settings.component';
import { InstanceManagementComponent } from './administration/settings/instance-management/instance-management.component';
import { SessionExitService } from 'src/core/services/user/session-exit.service';
import { eUserMenuItems } from '@volosoft/abp.ng.theme.lepton-x';
import { PermissionConstants } from 'src/core/constants/permission.constant';
import { installTenantDateSerialization } from 'src/core/helpers/tenant-date-serialization.helper';

@Component({
  selector: 'app-root',
  standalone: true,
  template: `
    <abp-loader-bar />
    <abp-dynamic-layout />
    <abp-gdpr-cookie-consent />
    <abp-internet-status />
    <p-toast position="bottom-right" [life]="4000" />
    <app-confirm-dialog-host />
    <app-user-skill-dialog />
    <app-assign-queue-dialog />
    <!--
      Same permission gate as the AgentStatusToolbar NavItem that starts the flow. Without it every
      user (login screen included) would bootstrap the CallFacade -> CallTabSync (opens a
      BroadcastChannel) -> AgentDesktopService chain at app start just to render nothing.
    -->
    @if (agentStatusAccess.isEnabled()) {
      <app-reject-call-dialog-host />
    }
  `,
  imports: [
    LoaderBarComponent,
    DynamicLayoutComponent,
    GdprCookieConsentComponent,
    InternetConnectionStatusComponent,
    Toast,
    ConfirmDialogHostComponent,
    UserSkillDialogComponent,
    AssignQueueDialogComponent,
    RejectCallDialogHostComponent,
  ],
})
export class AppComponent {
  private settingTabs = inject(SettingTabsService);
  private routesService = inject(RoutesService);
  private userMenuService = inject(UserMenuService);
  private navItemsService = inject(NavItemsService);
  protected agentStatusAccess = inject(AgentStatusAccessService);
  private replaceableComponentsService = inject(ReplaceableComponentsService);
  private modalService = inject(NgbModal);
  private userService = inject(UserService);
  private aiDisclaimerGate = inject(AiDisclaimerGateService);
  private oAuthService = inject(OAuthService);
  private configStateService = inject(ConfigStateService);
  private sessionExit = inject(SessionExitService);
  private timezoneService = inject(TimezoneService);
  private modalShown = false;
  private agentStatusNavItemsRegistered = false;

  constructor() {
    this.overrideOutgoingDateSerialization();

    this.settingTabs.add([
      {
        name: 'Settings::IntegrationSettings',
        component: IntegrationSettingsComponent,
      },
      {
        name: 'Settings::DataImport:Title',
        component: DataImportComponent,
        requiredPolicy: PermissionConstants.canManageDataImport,
      },
      {
        name: 'Settings::PhoneNumbers:Title',
        component: PhoneNumbersMainComponent,
        requiredPolicy: PermissionConstants.canManageDidRouting,
      },
      {
        name: 'Settings::WorkEnvironment:Title',
        component: WorkEnvironmentSettingsComponent,
        requiredPolicy: PermissionConstants.canManageWorkEnvironmentSettings,
      },
      {
        name: 'Settings::Queues:TabTitle',
        component: QueueManagementComponent,
        requiredPolicy: PermissionConstants.agentManagement,
      },
      {
        name: 'Settings::Localization:Title',
        component: LocalizationMainComponent,
        requiredPolicy: PermissionConstants.localizationSettingsTab,
      },
      {
        name: 'Settings::ScheduledCall:Title',
        component: ScheduledCallSettingsComponent,
        requiredPolicy: PermissionConstants.canManageScheduledCallSettings,
      },
    ]);

    this.settingTabs.remove(['AbpSettingManagement::Menu:TimeZone']);

    // Removed from the product for both host and tenant users; view is kept, only the menu entry goes away.
    this.routesService.remove([eIdentityRouteNames.OrganizationUnits]);

    this.replaceableComponentsService.add({
      component: FooterComponent,
      key: eThemeLeptonXComponents.Footer,
    });

    const tenantId = this.configStateService.getDeep(['currentUser', 'tenantId']);
    const isHost = tenantId == null;
    if (isHost) {
      this.settingTabs.add([
        {
          name: 'FreeswitchIntegration::InstanceManagement',
          component: InstanceManagementComponent,
        },
      ]);

      // Removed from the product for the ABP host user; tenants keep these tabs.
      this.settingTabs.remove([
        'Settings::IntegrationSettings',
        'Settings::DataImport:Title',
        'Settings::PhoneNumbers:Title',
        'Settings::WorkEnvironment:Title',
        'Settings::Queues:TabTitle',
        'Settings::Localization:Title',
        'Settings::ScheduledCall:Title',
      ]);

      this.routesService.remove(['::Menu:Contacts']);
    }
    void this.registerAgentStatusNavItems();
    this.registerNotificationNavItems();

    this.addUserMenuItems();
    this.patchLogoutAction();
    this.checkAndShowDisclaimer();
  }

  /** See `installTenantDateSerialization`. */
  private overrideOutgoingDateSerialization(): void {
    installTenantDateSerialization(() => this.timezoneService.timezone);
  }

  private addUserMenuItems(): void {
    this.userMenuService.addItems([
      new UserMenu({
        id: 'About',
        order: 105.5,
        action: () => this.openVersionModal(),
        textTemplate: { text: '::Menu:About', icon: 'fa fa-info-circle' },
      } as Partial<UserMenu>),
    ]);
  }

  private patchLogoutAction(): void {
    this.userMenuService.patchItem(eUserMenuItems.Logout, {
      action: () => {
        void this.sessionExit.logout();
      },
    });
  }

  private async registerAgentStatusNavItems(): Promise<void> {
    if (!this.agentStatusAccess.canUseStatusManagement() || this.agentStatusNavItemsRegistered) {
      return;
    }

    await firstValueFrom(
      this.configStateService.getAll$().pipe(filter(config => !!config?.currentUser)),
    );

    this.navItemsService.addItems([
      new NavItem({
        id: 'AgentStatusToolbar',
        component: AgentStatusToolbarComponent,
        order: 89,
      }),
      new NavItem({
        id: 'QuickAvailable',
        component: QuickAvailableComponent,
        order: 91,
      }),
    ]);

    this.agentStatusNavItemsRegistered = true;
  }

  private registerNotificationNavItems(): void {
    this.navItemsService.addItems([
      new NavItem({
        id: 'NotificationBell',
        component: NotificationBellComponent,
        order: 88,
      }),
    ]);
  }

  private openVersionModal(): void {
    this.modalService.open(VersionModalComponent, {
      centered: true,
      backdrop: true,
      keyboard: true,
    });
  }

  /**
   * Shows the AI usage disclaimer when the user has not confirmed it yet, and opens the AI disclaimer gate
   * only when the disclaimer is out of the way, so first-run screens such as the onboarding tour never open
   * stacked on top of it.
   */
  private checkAndShowDisclaimer(): void {
    if (this.modalShown) {
      return;
    }

    if (!this.oAuthService.hasValidAccessToken()) {
      this.aiDisclaimerGate.release();
      return;
    }

    this.userService.getConfirmedAiUsageDisclaimer().subscribe({
      next: confirmed => {
        if (confirmed) {
          this.aiDisclaimerGate.release();
        } else if (!this.modalShown) {
          this.showAiDisclaimerModal();
        }
      },
      error: error => {
        console.error('AI Disclaimer check failed:', error);
        // The disclaimer is not shown on a failed check, so nothing can stack; keep first-run screens working.
        this.aiDisclaimerGate.release();
      },
    });
  }

  private showAiDisclaimerModal(): void {
    this.modalShown = true;

    const modalRef = this.modalService.open(AiDisclaimerModalComponent, {
      size: 'lg',
      backdrop: 'static',
      keyboard: false,
      windowClass: 'ai-disclaimer-modal',
      centered: true,
    });

    // The dialog only closes after the confirmation is saved; a dismissal (e.g. leaving the page) keeps the
    // gate closed so the onboarding waits for the disclaimer again.
    modalRef.result
      .then(() => this.aiDisclaimerGate.release())
      .catch(() => undefined)
      .finally(() => {
        this.modalShown = false;
      });
  }
}
